import React, { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { AlertCircle, Plus, Trash2, Bell, Mail, Phone } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";

interface EmailPreferences {
  new_orders: {
    enabled: boolean;
    emails: Record<string, boolean>;
  };
  status_updates: {
    enabled: boolean;
    emails: Record<string, boolean>;
  };
}

const EnhancedNotificationSettings: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [storeProfile, setStoreProfile] = useState<any>(null);
  const [notificationEmails, setNotificationEmails] = useState<string[]>([]);
  const [notificationPhones, setNotificationPhones] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [emailPreferences, setEmailPreferences] = useState<EmailPreferences>({
    new_orders: { enabled: true, emails: {} },
    status_updates: { enabled: true, emails: {} }
  });

  useEffect(() => {
    if (user) {
      fetchStoreProfile();
    }
  }, [user]);

  const fetchStoreProfile = async () => {
    try {
      setLoading(true);
      const { data: storeProfileData, error } = await supabase
        .from('store_profiles')
        .select('*')
        .eq('florist_id', user?.id)
        .single();

      if (error) throw error;

      setStoreProfile(storeProfileData);
      setNotificationEmails(storeProfileData.notification_emails || []);
      setNotificationPhones(storeProfileData.notification_phones || []);
      
      // Load email preferences
      const existingPrefs = storeProfileData.email_notification_preferences || {
        new_orders: { enabled: true, emails: {} },
        status_updates: { enabled: true, emails: {} }
      };
      
      // Ensure all current emails have preferences set
      const allEmails = [...(storeProfileData.notification_emails || [])];
      if (storeProfileData.email) {
        allEmails.unshift(storeProfileData.email);
      }
      
      // Set default preferences for new emails
      ['new_orders', 'status_updates'].forEach(type => {
        if (!existingPrefs[type]) {
          existingPrefs[type] = { enabled: true, emails: {} };
        }
        allEmails.forEach(email => {
          if (!(email in existingPrefs[type].emails)) {
            existingPrefs[type].emails[email] = true; // Default to enabled
          }
        });
      });
      
      setEmailPreferences(existingPrefs);
      
    } catch (error: any) {
      console.error('Error fetching store profile:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const getAllEmails = () => {
    const emails = [...notificationEmails];
    if (storeProfile?.email && !emails.includes(storeProfile.email)) {
      emails.unshift(storeProfile.email);
    }
    return emails;
  };

  const validateEmail = (email: string) => {
    if (!email) return 'Email is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Invalid email format';
    return '';
  };

  const validatePhone = (phone: string) => {
    if (!phone) return 'Phone number is required';
    const phoneRegex = /^\+?[0-9]{10,15}$/;
    if (!phoneRegex.test(phone)) return 'Invalid phone format (10-15 digits)';
    return '';
  };

  const handleAddEmail = () => {
    const error = validateEmail(newEmail);
    setEmailError(error);
    
    if (!error) {
      if (!notificationEmails.includes(newEmail)) {
        const updatedEmails = [...notificationEmails, newEmail];
        setNotificationEmails(updatedEmails);
        
        // Add default preferences for new email
        const updatedPrefs = { ...emailPreferences };
        ['new_orders', 'status_updates'].forEach(type => {
          updatedPrefs[type as keyof EmailPreferences].emails[newEmail] = true;
        });
        setEmailPreferences(updatedPrefs);
        
        setNewEmail('');
      } else {
        setEmailError('This email is already added');
      }
    }
  };

  const handleAddPhone = () => {
    const error = validatePhone(newPhone);
    setPhoneError(error);
    
    if (!error) {
      if (!notificationPhones.includes(newPhone)) {
        setNotificationPhones([...notificationPhones, newPhone]);
        setNewPhone('');
      } else {
        setPhoneError('This phone number is already added');
      }
    }
  };

  const handleRemoveEmail = (email: string) => {
    // Don't allow removing store email
    if (email === storeProfile?.email) {
      setErrorMessage("Cannot remove store email. You can disable notifications for it instead.");
      setTimeout(() => setErrorMessage(''), 3000);
      return;
    }
    
    setNotificationEmails(notificationEmails.filter(e => e !== email));
    
    // Remove from preferences
    const updatedPrefs = { ...emailPreferences };
    ['new_orders', 'status_updates'].forEach(type => {
      delete updatedPrefs[type as keyof EmailPreferences].emails[email];
    });
    setEmailPreferences(updatedPrefs);
  };

  const handleRemovePhone = (phone: string) => {
    setNotificationPhones(notificationPhones.filter(p => p !== phone));
  };

  const handleToggleEmailNotification = (email: string, notificationType: keyof EmailPreferences, enabled: boolean) => {
    const updatedPrefs = { ...emailPreferences };
    updatedPrefs[notificationType].emails[email] = enabled;
    setEmailPreferences(updatedPrefs);
  };

  const handleToggleNotificationType = (notificationType: keyof EmailPreferences, enabled: boolean) => {
    const updatedPrefs = { ...emailPreferences };
    updatedPrefs[notificationType].enabled = enabled;
    setEmailPreferences(updatedPrefs);
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setSuccessMessage('');
      setErrorMessage('');
      
      if (!storeProfile) return;
      
      const { error } = await supabase
        .from('store_profiles')
        .update({
          notification_emails: notificationEmails,
          notification_phones: notificationPhones,
          email_notification_preferences: emailPreferences
        })
        .eq('id', storeProfile.id);
        
      if (error) throw error;
      
      setSuccessMessage('Notification settings saved successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      
    } catch (error: any) {
      console.error('Error saving notification settings:', error.message);
      setErrorMessage('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <div className="animate-spin h-6 w-6 border-2 border-primary rounded-full border-t-transparent"></div>
      </div>
    );
  }

  const allEmails = getAllEmails();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notification Settings
        </CardTitle>
        <CardDescription>
          Control who gets notified about different events. Everyone gets new order notifications by default.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {errorMessage && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}
        
        {successMessage && (
          <Alert variant="success" className="bg-green-50 border-green-500 text-green-900">
            <AlertDescription>{successMessage}</AlertDescription>
          </Alert>
        )}

        {/* Email Management Section */}
        <div>
          <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Email Addresses
          </h3>
          
          {storeProfile?.email && (
            <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-blue-900">Store email:</span>
                <span className="text-blue-700">{storeProfile.email}</span>
                <Badge variant="secondary" className="ml-auto">Primary</Badge>
              </div>
            </div>
          )}
          
          <div className="mb-4">
            {notificationEmails.length > 0 ? (
              <div className="space-y-2 mb-4">
                {notificationEmails.map((email) => (
                  <div key={email} className="flex items-center justify-between p-2 border rounded">
                    <span className="text-sm">{email}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">Staff</Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleRemoveEmail(email)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mb-4">
                No additional staff emails added
              </p>
            )}
          
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  placeholder="Enter staff email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                />
                {emailError && <p className="text-xs text-destructive mt-1">{emailError}</p>}
              </div>
              <Button onClick={handleAddEmail} className="bg-primary bg-[#967F81] hover:bg-[#843329]/90">
                <Plus className="h-4 w-4 mr-2" />
                Add Email
              </Button>
            </div>
          </div>
        </div>

        <Separator />



        {/* Notification Preferences Section */}
        <div>
          <h3 className="text-lg font-medium mb-4">Email Notification Preferences</h3>
          
          {/* New Orders Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <Label className="text-base font-medium">New Orders</Label>
                <p className="text-sm text-muted-foreground">Get notified when new orders are received</p>
              </div>
              <Switch
                checked={emailPreferences.new_orders.enabled}
                onCheckedChange={(checked) => handleToggleNotificationType('new_orders', checked)}
              />
            </div>
            
            {emailPreferences.new_orders.enabled && allEmails.length > 0 && (
              <div className="ml-4 space-y-2">
                {allEmails.map((email) => (
                  <div key={email} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{email}</span>
                      {email === storeProfile?.email && (
                        <Badge variant="secondary" className="text-xs">Store</Badge>
                      )}
                    </div>
                    <Switch
                      checked={emailPreferences.new_orders.emails[email] ?? true}
                      onCheckedChange={(checked) => handleToggleEmailNotification(email, 'new_orders', checked)}
                      size="sm"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Status Updates Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <Label className="text-base font-medium">Order Status Updates</Label>
                <p className="text-sm text-muted-foreground">Get notified when order status changes</p>
              </div>
              <Switch
                checked={emailPreferences.status_updates.enabled}
                onCheckedChange={(checked) => handleToggleNotificationType('status_updates', checked)}
              />
            </div>
            
            {emailPreferences.status_updates.enabled && allEmails.length > 0 && (
              <div className="ml-4 space-y-2">
                {allEmails.map((email) => (
                  <div key={email} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{email}</span>
                      {email === storeProfile?.email && (
                        <Badge variant="secondary" className="text-xs">Store</Badge>
                      )}
                    </div>
                    <Switch
                      checked={emailPreferences.status_updates.emails[email] ?? true}
                      onCheckedChange={(checked) => handleToggleEmailNotification(email, 'status_updates', checked)}
                      size="sm"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <Separator />
        
        {/* SMS Notifications Section */}
        <div>
          <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
            <Phone className="h-5 w-5 text-primary" />
            SMS Notifications
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Your store's primary phone number gets SMS notifications by default. Add additional numbers below.
          </p>
          
          {/* Primary Phone Display */}
          {storeProfile?.phone && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2 text-sm">
                <Phone className="h-4 w-4 text-green-600" />
                <span className="font-medium text-green-900">Primary phone:</span>
                <span className="text-green-700">{storeProfile.phone}</span>
                <Badge variant="secondary" className="ml-auto">SMS Enabled</Badge>
              </div>
              <p className="text-xs text-green-600 mt-1">This number receives SMS notifications for new orders automatically.</p>
            </div>
          )}
          
          {/* Additional Phone Numbers */}
          <div className="mb-4">
            <Label className="text-sm font-medium mb-2 block">Additional Phone Numbers</Label>
            {notificationPhones.length > 0 ? (
              <div className="space-y-2 mb-4">
                {notificationPhones.map((phone) => (
                  <div key={phone} className="flex items-center justify-between p-2 border rounded">
                    <span className="text-sm">{phone}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">Additional SMS</Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleRemovePhone(phone)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mb-4">
                No additional phone numbers added
              </p>
            )}
          
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  placeholder="Enter additional phone number (e.g., +61 4XX XXX XXX)"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddPhone()}
                />
                {phoneError && <p className="text-xs text-destructive mt-1">{phoneError}</p>}
              </div>
              <Button onClick={handleAddPhone} className="bg-primary bg-[#967F81] hover:bg-[#843329]/90">
                <Plus className="h-4 w-4 mr-2" />
                Add Phone
              </Button>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={handleSaveSettings} disabled={saving} className="bg-primary bg-[#967F81] hover:bg-[#843329]/90">
            {saving ? 
              <div className="flex items-center gap-2">
                <span className="animate-spin h-4 w-4 border-2 border-current rounded-full border-t-transparent"></span>
                Saving...
              </div> : 
              'Save Settings'
            }
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default EnhancedNotificationSettings; 